// Sponsor Banner — real Business Suite ad banner (the 40% platform / 40% creator-pool /
// 20% viewer-pool escrow model). Previously this component called a separate, older
// ad system (/ads/rotation + /ads/impression-v2, backed by db.campaigns) that paid out
// a flat 1 NX to the creator + 2 NX to the platform instantly per impression, no escrow,
// no viewer share at all -- a different, disconnected model from the one advertisers
// actually pay into via Business Suite (db.ad_campaigns). This now calls the real
// endpoints directly: no wallet writes happen here, impressions/clicks are just logged
// and the creator + viewer pools get divided pro-rata and paid out once, at the end of
// the campaign, via POST /business/campaigns/{id}/settle.
//
// `surface` must be one of the values a Business Suite campaign actually declares:
// "stream" (live streams), "reel" (reel viewer), or "boost" (profile / newsfeed).
import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { ExternalLink } from "lucide-react";

export default function SponsorBanner({ surface, creatorId, hideForOwner }) {
  const [ad, setAd] = useState(null);
  const lastAdId = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/business/ad/next", { params: { surface, creator_id: creatorId } });
      setAd(data || null);
      if (data && lastAdId.current !== data.id) {
        lastAdId.current = data.id;
        api.post("/business/ad/impression", { campaign_id: data.id, creator_id: creatorId || null, surface }).catch(() => {});
      }
    } catch { setAd(null); }
  };

  useEffect(() => {
    if (hideForOwner || !surface) return;
    load();
    // Re-fetch periodically so a long session still cycles through other active campaigns.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [surface, creatorId, hideForOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hideForOwner || !ad) return null;

  const trackClick = () => api.post("/business/ad/click", { campaign_id: ad.id, creator_id: creatorId || null }).catch(() => {});

  const hasCta = !!ad.cta_url;
  const inner = (
    <>
      <div className="absolute top-0 left-0 bg-nexus-amber text-nexus-obsidian px-2 py-0.5 text-[8px] tracking-[0.3em] font-display font-black uppercase">★ Sponsored</div>
      <div className="p-4 flex items-center gap-4">
        {ad.creative_url && <img src={ad.creative_url} alt="" className="w-14 h-14 object-cover ring-1 ring-nexus-amber/40 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-base font-display font-bold mt-0.5 truncate">{ad.name}</div>
          <div className="text-[11px] text-white/50 mt-0.5 truncate">{ad.headline || ad.cta_text || "Sponsored content"}</div>
        </div>
        {hasCta && (
          <ExternalLink className="w-4 h-4 text-nexus-amber group-hover:scale-110 transition-transform" data-testid="sponsor-ext-icon" />
        )}
      </div>
    </>
  );

  const cls = "relative block border border-nexus-amber/40 bg-gradient-to-r from-nexus-amber/15 via-nexus-amber/5 to-transparent overflow-hidden group hover:border-nexus-amber transition-colors";

  return hasCta ? (
    <a href={ad.cta_url} target="_blank" rel="noopener sponsored noreferrer" onClick={trackClick} className={cls} data-testid="sponsor-banner">{inner}</a>
  ) : (
    <div className={cls} data-testid="sponsor-banner">{inner}</div>
  );
}
